// src/pages/Study.jsx
// "Sticky Notes" pastel-cream redesign for the /study listing page.
// Drop-in replacement for the existing Study.jsx — keeps your /api/posts
// fetch, subject grouping, ThemeContext dark-mode support, and the
// Link-to-/study/:slug structure.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";

function StickyCard({ post: p, tint, rot, dark }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Link
      to={`/study/${p.slug}`}
      className="block relative no-underline"
      style={{
        background: tint.bg,
        padding: "26px 22px 22px",
        borderRadius: 6,
        minHeight: 180,
        transform: hovered ? "rotate(0deg) translateY(-4px)" : `rotate(${rot}deg)`,
        boxShadow: hovered
          ? `0 18px 32px ${tint.shadow}, 0 1px 0 rgba(0,0,0,0.04)`
          : `0 10px 22px ${tint.shadow}, 0 1px 0 rgba(0,0,0,0.04)`,
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* washi tape */}
      <span
        className="absolute"
        style={{
          top: -10,
          left: "50%",
          transform: "translateX(-50%) rotate(-3deg)",
          width: 64,
          height: 20,
          background: tint.tape,
          opacity: 0.7,
          borderRadius: 1,
        }}
      />
      <div
        className="text-[11px] font-bold tracking-[0.12em] mb-2"
        style={{ color: dark ? "#D4B888" : "#7A5A40" }}
      >
        {new Date(p.published_at)
          .toLocaleDateString(undefined, { month: "short", day: "2-digit" })
          .toUpperCase()}
      </div>
      <h3
        className="text-base md:text-[17px] font-bold leading-snug mb-2.5"
        style={{ color: dark ? "#F5E8D0" : "#3D3128" }}
      >
        {p.title}
      </h3>
      <p
        className="text-[13.5px] leading-relaxed m-0 line-clamp-3"
        style={{ color: dark ? "#D4B888" : "#5C4A3A" }}
      >
        {p.summary}
      </p>
    </Link>
  );
}

// Pastel-mix tints, cycled across all notes regardless of subject so the
// wall feels scrapbook-y rather than rigidly color-coded by subject.
const TINTS = [
  { bg: "#FFF1B8", shadow: "rgba(200, 160, 60, 0.18)", tape: "#F4D08C" }, // butter
  { bg: "#FFD9C4", shadow: "rgba(200, 110, 80, 0.18)", tape: "#F2A88E" }, // peach
  { bg: "#D9F0D4", shadow: "rgba(80, 150, 90, 0.18)", tape: "#A6D6A0" }, // mint
  { bg: "#E5D9F2", shadow: "rgba(130, 110, 180, 0.18)", tape: "#BDA8DC" }, // lavender
  { bg: "#FFD6E0", shadow: "rgba(210, 110, 140, 0.18)", tape: "#F2A8B8" }, // blush
  { bg: "#CFE7F2", shadow: "rgba(80, 140, 180, 0.18)", tape: "#9CC6DC" }, // sky
];

// Dark-mode-equivalent tints (muted, lower saturation) so the cute
// pastel feel survives without burning eyes at night.
const DARK_TINTS = [
  { bg: "#3D3520", shadow: "rgba(0,0,0,0.4)", tape: "#7A6A40" },
  { bg: "#3D2820", shadow: "rgba(0,0,0,0.4)", tape: "#7A4D38" },
  { bg: "#1F3328", shadow: "rgba(0,0,0,0.4)", tape: "#3D6650" },
  { bg: "#2A2440", shadow: "rgba(0,0,0,0.4)", tape: "#544870" },
  { bg: "#3D2030", shadow: "rgba(0,0,0,0.4)", tape: "#704050" },
  { bg: "#1F2A38", shadow: "rgba(0,0,0,0.4)", tape: "#385570" },
];

const SOCIAL = [
  { label: "YouTube", href: "https://www.youtube.com/@Caffei_N", icon: "📺" },
  { label: "Facebook", href: "https://facebook.com/caffeiN0w0", icon: "👥" },
  { label: "Pinterest", href: "https://www.pinterest.com/caffei_n", icon: "📌" },
];

export default function Study() {
  const [posts, setPosts] = useState([]);
  const { dark } = useTheme();

  useEffect(() => {
    fetch("/api/posts")
      .then((r) => r.json())
      .then(setPosts)
      .catch(() => {});
  }, []);

  // Group posts by subject preserving first-seen order
  const { subjects, bySubject } = useMemo(() => {
    const order = [];
    const map = {};
    posts.forEach((p) => {
      const key = p.subject || "General";
      if (!map[key]) {
        order.push(key);
        map[key] = [];
      }
      map[key].push(p);
    });
    return { subjects: order, bySubject: map };
  }, [posts]);

  const tints = dark ? DARK_TINTS : TINTS;

  return (
    <div
      className="min-h-screen transition-colors duration-300 font-[Quicksand]"
      style={{ background: dark ? "#1A1612" : "#FFF8EC" }}
    >
      <title>Study Notes | 5 Minute Nursing School</title>
      <meta name="description" content="Browse free nursing study notes — pharmacology, anatomy, fundamentals, and more. Concise summaries for fast review." />
      <link rel="canonical" href="https://5minutenursingschool.com/study" />
      {/* Hero */}
      <header className="text-center pt-20 pb-10 px-4">
        <div
          className="inline-block px-5 py-1.5 rounded-full text-xs font-bold tracking-[0.18em] mb-4"
          style={{
            background: dark ? "#3D2820" : "#FFD9C4",
            color: dark ? "#F2A88E" : "#A04030",
          }}
        >
          ✿ STUDY NOTES ✿
        </div>
        <h1
          className="text-5xl md:text-6xl font-bold tracking-tight"
          style={{ color: dark ? "#F5E8D0" : "#3D3128" }}
        >
          my little notebook
        </h1>
        <p
          className="mt-3 text-base md:text-lg"
          style={{ color: dark ? "#A88862" : "#8B7560" }}
        >
          everything i'm trying to remember, made cute on purpose
        </p>
      </header>

      {/* Posts grouped by subject */}
      <main className="max-w-5xl mx-auto px-6 pb-20">
        {subjects.length === 0 && (
          <p
            className="text-center py-16 text-lg"
            style={{ color: dark ? "#7A6A4E" : "#A88862" }}
          >
            No notes yet — check back soon ✿
          </p>
        )}

        {subjects.map((subject, sIdx) => {
          const items = bySubject[subject];
          return (
            <section key={subject} className="mb-12">
              {/* Subject row: dot + name + solid divider + N notes */}
              <div className="flex items-baseline gap-4 mb-5">
                <h2
                  className="text-xl md:text-2xl font-bold m-0 flex items-center gap-3"
                  style={{ color: dark ? "#F5E8D0" : "#5C4A3A" }}
                >
                  <span
                    className="inline-block w-2 h-2 rounded-full"
                    style={{ background: "#F2A88E" }}
                  />
                  {subject}
                </h2>
                <span
                  className="flex-1 h-0.5 rounded-sm"
                  style={{ background: dark ? "#3D2820" : "#FFE4D0" }}
                />
                <span
                  className="text-sm font-semibold whitespace-nowrap"
                  style={{ color: dark ? "#A88862" : "#A88862" }}
                >
                  {items.length} {items.length === 1 ? "note" : "notes"}
                </span>
              </div>

              {/* Sticky-note grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
                {items.map((p, i) => {
                  // Stable per-card index across the whole feed so colors don't
                  // re-shuffle as new posts arrive in unrelated subjects.
                  const idx = posts.indexOf(p);
                  const tint = tints[idx % tints.length];
                  const rot = (idx % 2 === 0 ? -1 : 1) * (1 + (idx % 3) * 0.4);
                  return (
                    <StickyCard key={p.slug} post={p} tint={tint} rot={rot} dark={dark} />
                  );
                })}
              </div>
            </section>
          );
        })}
      </main>

      {/* Footer */}
      <footer
        className="border-t py-8 px-6"
        style={{
          borderColor: dark ? "#2A2218" : "#F0E0C8",
          background: dark ? "#0F0C08" : "#fff",
        }}
      >
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <p
              className="font-semibold m-0"
              style={{ color: dark ? "#D4B888" : "#5C4A3A" }}
            >
              5 Minute Nursing School
            </p>
            <p
              className="text-sm m-0"
              style={{ color: dark ? "#7A6A4E" : "#A88862" }}
            >
              📍 Dhaka, Bangladesh
            </p>
          </div>
          <div className="flex gap-5">
            {SOCIAL.map(({ label, href, icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                className="text-2xl hover:scale-110 transition-transform"
              >
                {icon}
              </a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
